import { Doughnut } from 'react-chartjs-2';
import Chart, { ArcElement } from 'chart.js/auto';
import React, { useEffect, useMemo, useState } from 'react';
import { useExchangeRates } from '../../context/ExchangeRateContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { num, formatCryptoUsd } from '../../utils/cryptoDecimal';
Chart.register(ArcElement);

interface PortfolioChartBoxProps {
  portfolios: any[];
  currentPrices?: { [symbol: string]: number };
  useSubPorts?: boolean;
  fromAdmin?: boolean;
  currency?: string;
  loading?: boolean;
  cashAmount?: number | string; // พอร์ตคริปโตหลังบ้านส่งเป็น decimal string
  includeCash?: boolean; // ✅ เพิ่ม prop ใหม่เพื่อควบคุมการรวมกำไรที่ขายแล้ว
  layout?: 'vertical' | 'horizontal'; // ✅ เพิ่ม Prop นี้ ('vertical' = ข้างล่าง, 'horizontal' = ข้างขวา)
  isCrypto?: boolean; // พอร์ตคริปโต: ชื่อเหรียญแบบ BTC และยอดเล็กมากไม่ปัดเป็น 0.00
}

const COLORS = ['#3b82f6', '#22c55e', '#f59e42', '#e11d48', '#a855f7'];

const BASE_HUES = [
  217, // Blue (จาก #3b82f6)
  142, // Green (จาก #22c55e)
  31,  // Orange (จาก #f59e42)
  347, // Red (จาก #e11d48)
  271, // Purple (จาก #a855f7)
  // 173, // Teal (สีน้ำทะเล)
  // 330, // Pink (สีชมพู)
  // 45,  // Yellow (สีเหลืองตุ่น)
  // 239, // Indigo (สีคราม)
  // 190, // Cyan (สีฟ้าอ่อน)
];

// ความสว่าง 6 ระดับ (ค่าน้อย = เข้มมาก, ค่ามาก = อ่อนมาก)
const LIGHTNESS_LEVELS = [ 50, 55,60, 65,70];

const PortfolioChartBox: React.FC<PortfolioChartBoxProps> = ({
  portfolios,
  currentPrices,
  useSubPorts = false,
  fromAdmin = false, // ✅ มั่นใจว่ารับ prop นี้มา
  currency = 'USD',
  loading = false,
  cashAmount: rawCashAmount = 0,
  layout = 'vertical',
  isCrypto = false,
}) => {
  const money = (v: number) => (isCrypto ? formatCryptoUsd(v) : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  // แปลงเป็น number ก่อนบวก ไม่งั้น string จะถูกต่อกันแทนการบวก (ใช้แสดงผลเท่านั้น)
  const cashAmount = num(rawCashAmount);
  const { exchangeRates } = useExchangeRates();
  const exchangeRate = currency === 'THB' ? exchangeRates['THB'] || 1 : 1;
  const { displayMode: contextMode } = usePortfolio();
  const CASH_COLOR = '#065f46';
  // ✅ ถ้าเป็นจากแอดมิน ให้บังคับใช้ V2 เสมอ แม้ User จะตั้งเป็น V1
  const displayMode = fromAdmin ? 'V2' : contextMode;
  const [includeCash, setincludeCash] = useState(() => {
    const saved = localStorage.getItem('includeCash');
    return saved ? JSON.parse(saved) : true;
  });
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('includeCash');
      if (saved !== null) {
        setincludeCash(JSON.parse(saved));
      }
    };

    // ฟัง event ที่เราเพิ่ง dispatch ออกไป
    window.addEventListener('storage', handleStorageChange);

    // ล้าง event เมื่อ component ถูกทำลาย
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  // 1. คำนวณ Value และ Cost แบบ Unified Logic
  // 1. คำนวณ Stats แยกส่วนเพื่อไม่ให้กระทบ Logic เดิม
  const stats = useMemo(() => {
    let totalStockMarketValue = 0;
    let totalCostValue = 0;
    let totalRealizedGain = 0;

    portfolios.forEach((p: any) => {
      totalRealizedGain += num(p.summary?.realizedGain);

      // ✅ แยก Logic V1 / V2 ให้ชัดเจนเหมือนเดิม
      const items = displayMode === 'V1'
        ? (p.subPorts?.flatMap((sub: any) => sub.stocks) || p.stocks || [])
        : (p.holdings || p.stocks || []);

      items.forEach((item: any) => {
        // stockSymbol ก่อน: holding คริปโตถูกปรับเป็น X:...USD ไว้ตรงนี้ (ดู normalizeCryptoPortfolio)
        const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
        const quantity = num(item.quantity);
        const price = currentPrices?.[symbol] ?? 0;
        const avgPrice = num(item.avgEntryPrice || item.avgBuyPrice);

        totalStockMarketValue += quantity * price;
        totalCostValue += quantity * avgPrice;
      });
    });

    const unrealizedProfit = totalStockMarketValue - totalCostValue;
    // ✅ ใช้เงื่อนไขที่รับเข้ามา
    const finalProfit = includeCash ? (unrealizedProfit + totalRealizedGain) : unrealizedProfit;
    const finalProfitPercent = totalCostValue > 0 ? (finalProfit / totalCostValue) * 100 : 0;
    // ✅ กำไรสำหรับตัวเลขใหญ่ (รวม Realized)
    const totalCombinedProfit = (totalStockMarketValue - totalCostValue) + totalRealizedGain;
    const totalProfitPercent = totalCostValue > 0 ? (totalCombinedProfit / totalCostValue) * 100 : 0;

    // ✅ กำไรสำหรับค่าเดิม (คงไว้เพื่อไม่ให้กระทบ Condition อื่น)
    const originalProfit = unrealizedProfit;
    const originalProfitPercent = totalCostValue > 0 ? (originalProfit / totalCostValue) * 100 : 0;

    return {
      totalStockMarketValue,
      totalCostValue,
      // ค่าใหม่ที่เพิ่มเข้ามา
      totalCombinedProfit,
      totalProfitPercent,
      // ค่าเดิมที่คงไว้
      totalProfitPercentCombined: totalProfitPercent, // ✅ แก้ชื่อให้ตรงกับที่เรียกใช้
      originalProfit,
      originalProfitPercent,
      totalRealizedGain,
      finalProfit,          // กำไรที่ปรับตาม Toggle แล้ว
      finalProfitPercent    // เปอร์เซ็นต์ที่ปรับตาม Toggle แล้ว
    };
  }, [portfolios, currentPrices, displayMode, includeCash]);

  // 2. คำนวณค่าสำหรับแสดงผล
  const totalValue = includeCash
    ? (stats.totalStockMarketValue + cashAmount) * exchangeRate
    : stats.totalStockMarketValue * exchangeRate;
  const overallProfit = stats.totalCombinedProfit * exchangeRate;
  const overallProfitPercent = stats.totalProfitPercent;


  // ฟังก์ชันสร้างสี Multi-shade
  const generateMultiShadeColor = (index: number) => {
    const hue = BASE_HUES[index % BASE_HUES.length];
    const lightnessIndex = Math.floor(index / BASE_HUES.length) % LIGHTNESS_LEVELS.length;
    const lightness = LIGHTNESS_LEVELS[lightnessIndex];
    return `hsl(${hue}, 80%, ${lightness}%)`;
  };

  // 3. เตรียมข้อมูลกราฟ
  const isDefaultView = portfolios.length === 1;
  let chartLabels: string[] = [];
  let chartDataValues: number[] = [];
  let chartColors: string[] = [];

  if (isDefaultView) {
    if (displayMode === 'V1') {
      // ถ้าดูพอร์ตเดียวของ V1 ให้แสดงหุ้นข้างในพอร์ตหรือ subPorts
      const target = portfolios[0].subPorts?.length > 0 ? portfolios[0].subPorts : (portfolios[0].stocks || []);
      chartLabels = target.map((i: any) => i.name || i.stockSymbol);
      chartDataValues = target.map((i: any) => (i.totalValue || (i.quantity * (currentPrices?.[i.stockSymbol] || 0))) * exchangeRate);
    } else {
      // V2 เดิม
      const items = portfolios[0].holdings || portfolios[0].stocks || [];
      chartLabels = items.map((s: any) => (isCrypto && s.displaySymbol) || s.stockSymbol || s.assetSymbol);
      chartDataValues = items.map((s: any) => (num(s.quantity) * (currentPrices?.[s.stockSymbol || s.assetSymbol] ?? 0)) * exchangeRate);
    }
chartColors = chartLabels.map((_, i: number) => generateMultiShadeColor(i));
  } else {
    // กรณีมีหลายพอร์ต (แสดงภาพรวมพอร์ต)
    chartLabels = portfolios.map(p => p.name);
    chartDataValues = portfolios.map(p => {
      const items = displayMode === 'V1'
        ? (p.subPorts?.flatMap((sub: any) => sub.stocks) || p.stocks || [])
        : (p.holdings || p.stocks || p.subPorts?.flatMap((sub: any) => sub.holdings || sub.stocks || []) || []);

      const portStockValue = items.reduce((sum: number, item: any) => {
        const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
        const price = currentPrices?.[symbol] || 0;
        return sum + (num(item.quantity) * price);
      }, 0);

      return (portStockValue) * exchangeRate;
    }); chartColors = portfolios.map((_, i: number) => generateMultiShadeColor(i));
  }

  // 4. ผนวก Cash เข้าไปในกราฟ
  if (includeCash && cashAmount > 0) {
    chartLabels.push('เงินสด');
    chartDataValues.push(cashAmount * exchangeRate);
    chartColors.push(CASH_COLOR);
  }

  const chartData = {
    labels: chartLabels,
    datasets: [{
      data: chartDataValues,
      backgroundColor: chartColors, // ใส่ตรงๆ ไม่ต้องสร้าง Gradient
      borderWidth: 1,           // ✅ กำหนดความหนาของเส้นขอบ (เช่น 2 หรือ 3)
      borderColor: '#18181b',   // ✅ กำหนดสีเส้นขอบเป็นสีดำ/เทาเข้ม (หรือใช้สีโค้ดอื่นตามชอบ)
    }],
  };

  return (
    <div className="rounded-xl shadow-lg p-4 h-full flex flex-col items-center justify-center w-full"
    // style={{ backgroundColor: 'var(--component-bg-color)' }}
    >

      <div className={`flex ${layout === 'horizontal' ? 'flex-row items-center justify-between gap-4 w-full' : 'flex-col items-center justify-center'} w-full`}>

        {/* ✅ 1. ลดขนาด Donut ให้เล็กลงในโหมด horizontal เพื่อไม่ให้ล้นกรอบ */}
        <div className={`relative flex items-center justify-center shrink-0 mx-auto ${layout === 'horizontal'
            ? 'w-40 h-40 md:w-44 md:h-44'
            : (useSubPorts ? 'w-56 h-56 md:w-72 md:h-72' : 'w-64 h-64 md:w-96 md:h-96')
          }`}>
          <Doughnut
            data={chartData}
            options={{
              cutout:  '80%',
              plugins: { legend: { display: false } },
            }}
          />

          {/* วงกลมตรงกลางสีดำ (ปรับขนาดให้เล็กลงตามสัดส่วนใน horizontal) */}
          <div className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none rounded-full  m-auto ${layout === 'horizontal' ? 'w-20 h-20 md:w-24 md:h-24' : 'w-32 h-32 md:w-44 md:h-44'
            }`}>
           
              <>
                <span className={`font-bold text-white ${layout === 'horizontal' ? 'text-lg md:text-xl' : 'text-2xl md:text-3xl'}`} style={{ color: 'var(--text-color)' }}>
                  {currency === 'THB' ? '฿' : '$'}{money(totalValue)}
                </span>
                <span className={`font-semibold ${overallProfit >= 0 ? 'text-green-500' : 'text-red-500'} ${layout === 'horizontal' ? 'text-sm md:text-base' : 'text-base md:text-xl'}`}>
                  {overallProfit >= 0 ? '+' : ''}{currency === 'THB' ? '฿' : '$'}{money(overallProfit)}
                </span>
                <span className={`font-semibold ${overallProfitPercent >= 0 ? 'text-green-500' : 'text-red-500'} ${layout === 'horizontal' ? 'text-sm md:text-sm' : 'text-base md:text-lg'}`}>
                  ({overallProfitPercent >= 0 ? '+' : ''}{overallProfitPercent.toFixed(2)}%)
                </span>
              </>
            
          </div>
        </div>

        {/* ส่วน Label (Legend) */}
        <div className={`flex ${layout === 'horizontal' ? 'flex-col gap-1 items-start flex-1 overflow-hidden' : 'flex-wrap justify-center gap-x-4 gap-y-2 mt-4'}`}>
          {(() => {
            const allItems = chartLabels.map((label, idx) => ({
              label,
              color: chartColors[idx],
              value: chartDataValues[idx],
              isCash: label === 'เงินสด'
            }));

            const cashItem = allItems.find(item => item.isCash);
            const stockItems = allItems.filter(item => !item.isCash);
            const sortedStocks = [...stockItems].sort((a, b) => b.value - a.value);

            // ✅ 2. กำหนดจำนวนการแสดงผล: ถ้าเป็น horizontal ให้แสดงแค่ 5-6 ตัว (เช่น 5 ตัว + ...), ถ้า vertical แสดง 15 ตัว
            const maxDisplay = layout === 'horizontal' ? 5 : 15;
            const displayStocks = sortedStocks.slice(0, maxDisplay);
            const hasMoreStocks = sortedStocks.length > maxDisplay;

            let finalItems = [...displayStocks];
            if (hasMoreStocks) {
              finalItems.push({ label: '...', color: '#6b7280', value: 0, isCash: false });
            }
            if (cashItem) {
              finalItems.push(cashItem);
            }

            return (
              <>
                {finalItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }}></span>
                    <span className="text-xs md:text-sm truncate max-w-[110px]" style={{ color: 'var(--text-color)' }}>{item.label}</span>
                  </div>
                ))}
              </>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

export default PortfolioChartBox;